// Display-only unit conversion. Everything stored and calculated stays
// imperial (miles, mph, feet, lbs); values are converted only where they are
// shown, spoken or typed in. Imperial accounts get their input back unchanged,
// so existing call-site rounding and text are preserved exactly.

const KM_PER_MI = 1.609344;
const M_PER_FT = 0.3048;
const KG_PER_LB = 0.45359237;
const KG_PER_SHORT_TON = 907.18474;

let system = 'imperial';

export function setUnitSystem(value) {
    system = value === 'metric' ? 'metric' : 'imperial';
}

export function isMetric() {
    return system === 'metric';
}

// mode 'up'/'down' lets clearance checks round in the safe direction:
// the rig's height up, a posted clearance or weight limit down.
function roundTo(value, step, mode = 'nearest') {
    const n = value / step;
    const r = mode === 'up' ? Math.ceil(n - 1e-9)
        : mode === 'down' ? Math.floor(n + 1e-9)
        : Math.round(n);
    return Number((r * step).toFixed(3));
}

export function distance(miles) {
    if (!isMetric()) return miles;
    const km = miles * KM_PER_MI;
    return km < 10 ? roundTo(km, 0.1) : Math.round(km);
}

// Distance as text: imperial exactly as call sites have always written it
// ("12.3 miles", "4 mi"), metric as km with the usual rounding.
export function distanceText(miles, { decimals = null, long = false } = {}) {
    if (!isMetric()) {
        const v = decimals === null ? miles : Number(miles).toFixed(decimals);
        return `${v} ${long ? 'miles' : 'mi'}`;
    }
    return `${distance(Number(miles))} km`;
}

// Elevation as text: "11000 ft" or "3353 m".
export function elevationText(feet) {
    return isMetric() ? `${elevation(Number(feet))} m` : `${feet} ft`;
}

// A route's distance text: the routing service's own ("554 miles") for
// imperial, recomputed from distanceValue (miles) for metric.
export function routeDistanceText(route) {
    if (!isMetric() || typeof route?.distanceValue !== 'number') return route?.distance;
    return `${distance(route.distanceValue)} km`;
}

export function speed(mph) {
    return isMetric() ? Math.round(mph * KM_PER_MI) : mph;
}

export function height(feet, mode = 'nearest') {
    return isMetric() ? roundTo(feet * M_PER_FT, 0.1, mode) : feet;
}

export function length(feet) {
    return isMetric() ? roundTo(feet * M_PER_FT, 0.1) : feet;
}

export function elevation(feet) {
    return isMetric() ? Math.round(feet * M_PER_FT) : feet;
}

// Vehicle weights in kg, as Australian caravan specs quote them.
export function weight(lbs) {
    return isMetric() ? roundTo(lbs * KG_PER_LB, 10) : lbs;
}

// Posted road and bridge limits stay in tonnes, as the signs show them.
export function weightLimit(tons, mode = 'down') {
    return isMetric() ? roundTo(tons * KG_PER_SHORT_TON / 1000, 0.1, mode) : tons;
}

export const unit = {
    distance: (style = 'short') => (isMetric() ? 'km' : style === 'long' ? 'miles' : 'mi'),
    speed: () => (isMetric() ? 'km/h' : 'mph'),
    height: () => (isMetric() ? 'm' : 'ft'),
    weight: () => (isMetric() ? 'kg' : 'lbs'),
    weightLimit: () => (isMetric() ? 't' : 'ton')
};

export const spokenUnit = {
    distance: () => (isMetric() ? 'kilometres' : 'miles'),
    speed: () => (isMetric() ? 'kilometres per hour' : 'mile per hour'),
    height: () => (isMetric() ? 'metres' : 'feet'),
    weightLimit: () => (isMetric() ? 'tonne' : 'ton')
};

// Inputs: turn what a metric user typed back into the stored imperial value.
export function toMiles(value) {
    return isMetric() ? value / KM_PER_MI : value;
}

export function toMph(value) {
    return isMetric() ? value / KM_PER_MI : value;
}

export function toFeet(value) {
    return isMetric() ? value / M_PER_FT : value;
}

export function toLbs(value) {
    return isMetric() ? value / KG_PER_LB : value;
}
